/**
 * New work arriving on the wall (Part 04 Task 10, Q5, Q6).
 *
 * - A card that enters the project feed above every card already shown
 *   is an arrival: the masonry puts it at the top of the shortest column, and it rises into place (14px, 350 ms) and flashes.
 * - While the wall is scrolled down the view stays put and a "New ↑ N" pill
 *   counts the arrivals; it goes at the top of the wall.
 * - An agent's `keep` presentation flashes cards already on the wall
 *   (`flashWallArrival`).
 * - Updates never reorder: the feed is ordered by when items were made.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { onWallArrival } from "@/lib/wall-arrivals";
import type { WallMasonryHandle } from "./wall-masonry";

const WALL_RISE_MS = 350;
/** The card's flash ring (`animate-wall-flash`). */
const WALL_FLASH_MS = 1_400;

const NO_IDS: ReadonlySet<string> = new Set();

export interface WallArrivals {
  flashingIds: ReadonlySet<string>;
  risingIds: ReadonlySet<string>;
  /** Arrivals above the view; 0 hides the pill. */
  newCount: number;
  flash: (ids: readonly string[]) => void;
  /** The wall scrolled: at its top the pill goes. */
  onScroll: () => void;
  /** The pill: back to the top, where the new cards are. */
  showNew: () => void;
}

interface WallArrivalsOptions {
  ids: readonly string[];
  /** Follow arrivals (the project feed without a search). */
  tracking: boolean;
  /** The feed's first page is on its way: its cards are not arrivals. */
  loading: boolean;
  masonryRef: RefObject<WallMasonryHandle | null>;
}

/** Ids that stay in the set for `durationMs` after they were added. */
function useTimedIds(
  durationMs: number,
): [ReadonlySet<string>, (ids: readonly string[]) => void] {
  const [ids, setIds] = useState(NO_IDS);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const add = useCallback(
    (added: readonly string[]) => {
      if (added.length === 0) return;
      setIds((current) => new Set([...current, ...added]));
      for (const id of added) {
        clearTimeout(timers.current.get(id));
        timers.current.set(
          id,
          setTimeout(() => {
            timers.current.delete(id);
            setIds((current) => {
              if (!current.has(id)) return current;
              const rest = new Set(current);
              rest.delete(id);
              return rest.size > 0 ? rest : NO_IDS;
            });
          }, durationMs),
        );
      }
    },
    [durationMs],
  );
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);
  return [ids, add];
}

/** Ids before the first card already shown that were not shown: arrivals. */
function wallArrivalIds(
  ids: readonly string[],
  shown: ReadonlySet<string>,
): string[] {
  const arrivals: string[] = [];
  for (const id of ids) {
    if (shown.has(id)) break;
    arrivals.push(id);
  }
  return arrivals;
}

export function useWallArrivals({
  ids,
  tracking,
  loading,
  masonryRef,
}: WallArrivalsOptions): WallArrivals {
  const [flashingIds, flash] = useTimedIds(WALL_FLASH_MS);
  const [risingIds, rise] = useTimedIds(WALL_RISE_MS);
  const [newCount, setNewCount] = useState(0);
  const shownRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    const shown = shownRef.current;
    shownRef.current = tracking && !loading ? new Set(ids) : null;
    if (!shown || !shownRef.current) {
      setNewCount(0);
      return;
    }
    const arrivals = wallArrivalIds(ids, shown);
    if (arrivals.length === 0) return;
    rise(arrivals);
    flash(arrivals);
    if (masonryRef.current?.scrolledFromTop()) {
      setNewCount((count) => count + arrivals.length);
    }
  }, [flash, ids, loading, masonryRef, rise, tracking]);

  useEffect(() => onWallArrival(({ frameIds }) => flash(frameIds)), [flash]);

  const onScroll = useCallback(() => {
    setNewCount((count) =>
      count > 0 && !masonryRef.current?.scrolledFromTop() ? 0 : count,
    );
  }, [masonryRef]);
  const showNew = useCallback(() => {
    masonryRef.current?.scrollToTop();
    setNewCount(0);
  }, [masonryRef]);

  return { flashingIds, risingIds, newCount, flash, onScroll, showNew };
}
